import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Buildings, Plus, Trash, ArrowsClockwise, X, Stack, EnvelopeSimple, FilePdf } from "@phosphor-icons/react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, ReferenceLine, BarChart, Bar, CartesianGrid, Legend } from "recharts";
import api, { formatApiError, API_BASE } from "../lib/api";
import { formatCurrency } from "../lib/currency";
import { useAuth } from "../lib/auth";
import FiscalPresetChips from "../components/FiscalPresetChips";
import DatePicker from "../components/DatePicker";

const TABS = ["Groups", "Consolidated P&L", "Consolidated BS", "Consolidated Cash"];

export default function ConsolidationPage() {
  const { user } = useAuth();
  const isSuperuser = user?.role === "superuser";
  const [tab, setTab] = useState("Groups");
  const [groups, setGroups] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/consolidation/groups");
      setGroups(r.data || []);
      if (!selectedId && r.data?.[0]) setSelectedId(r.data[0].id);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="p-6 sm:p-8" data-testid="consolidation-page">
      <div className="mb-6">
        <div className="font-mono text-[10px] tracking-[0.2em] text-zinc-400">MODULE / CN · MULTI-ENTITY CONSOLIDATION</div>
        <h1 className="font-display text-4xl font-black tracking-tighter mt-1 flex items-center gap-3">
          Consolidation.
          <span className="text-xs font-mono px-2 py-1 bg-[#FF3E00] text-white uppercase tracking-widest">GROUP REPORTING</span>
        </h1>
        <div className="text-sm text-zinc-500 mt-2 max-w-3xl">
          Roll up P&L, Balance Sheet, and cash across multiple tenants into a single reporting currency. FX translation uses the CBO&nbsp;→&nbsp;override&nbsp;→&nbsp;market rate hierarchy. Intercompany transactions flagged <span className="font-mono">is_intercompany:true</span> are eliminated.
        </div>
      </div>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <label className="label-sm text-zinc-500">GROUP</label>
        <select
          value={selectedId || ""}
          onChange={(e) => setSelectedId(e.target.value)}
          className="border border-zinc-950 bg-white px-2 py-1 text-sm"
          data-testid="consolidation-group-select"
        >
          <option value="">— Select a group —</option>
          {groups.map((g) => (
            <option key={g.id} value={g.id}>{g.name} · {g.reporting_currency}</option>
          ))}
        </select>
        <button
          onClick={load}
          className="text-[10px] font-mono border border-zinc-950 px-3 py-1 hover:bg-zinc-950 hover:text-white flex items-center gap-1"
          data-testid="consolidation-refresh"
        >
          <ArrowsClockwise size={12} /> REFRESH
        </button>
        <BoardSnapshotActions groupId={selectedId} />
      </div>

      <div className="flex border-b border-zinc-950 mb-0 overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t}
            data-testid={`consolidation-tab-${t.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
            onClick={() => setTab(t)}
            className={`px-4 py-2.5 text-xs font-semibold tracking-[0.1em] uppercase border-r border-zinc-950 transition-colors whitespace-nowrap ${
              tab === t ? "bg-zinc-950 text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"
            }`}
          >
            {t}
          </button>
        ))}
      </div>
      <div className="border-x border-b border-zinc-950 min-h-[55vh]">
        {tab === "Groups" && <GroupsPanel groups={groups} loading={loading} isSuperuser={isSuperuser} onChange={load} />}
        {tab === "Consolidated P&L" && <ConsolidatedPLPanel groupId={selectedId} />}
        {tab === "Consolidated BS" && <ConsolidatedBSPanel groupId={selectedId} />}
        {tab === "Consolidated Cash" && <ConsolidatedCashPanel groupId={selectedId} />}
      </div>
      {selectedId && (
        <>
          <HistoryChart groupId={selectedId} cur={groups.find((g) => g.id === selectedId)?.reporting_currency || "OMR"} />
          <CashAlertConfigPanel
            groupId={selectedId}
            isSuperuser={isSuperuser}
            parentTenantId={groups.find((g) => g.id === selectedId)?.parent_tenant_id}
            currentUserTenantId={user?.tenant?.tenant_id}
          />
        </>
      )}
    </div>
  );
}

function GroupsPanel({ groups, loading, isSuperuser, onChange }) {
  const [form, setForm] = useState({ name: "", parent_tenant_id: "", reporting_currency: "OMR" });
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [details, setDetails] = useState({});
  const [newMember, setNewMember] = useState({});
  const [tenants, setTenants] = useState([]);

  // Precompute non-member tenants per group so the JSX doesn't rebuild the
  // filtered array on every render.
  const nonMemberByGroup = useMemo(() => {
    const map = {};
    for (const g of groups) {
      const memberSet = new Set(g.member_tenant_ids || []);
      map[g.id] = tenants.filter((t) => !memberSet.has(t.tenant_id));
    }
    return map;
  }, [groups, tenants]);

  useEffect(() => {
    if (!isSuperuser) return;
    api.get("/platform/tenants").then((r) => {
      const list = Array.isArray(r.data) ? r.data : (r.data?.items || []);
      setTenants(list.map((t) => ({ tenant_id: t.tenant_id, name: t.name || t.tenant_id })));
    }).catch(() => {});
  }, [isSuperuser]);

  const create = async (e) => {
    e.preventDefault();
    if (!form.name || !form.parent_tenant_id) return;
    setBusy(true);
    try {
      await api.post("/consolidation/groups", { ...form, member_tenant_ids: [form.parent_tenant_id] });
      toast.success(`Group '${form.name}' created`);
      setForm({ name: "", parent_tenant_id: "", reporting_currency: "OMR" });
      await onChange();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const expand = async (gid) => {
    if (expanded === gid) { setExpanded(null); return; }
    setExpanded(gid);
    try {
      const r = await api.get(`/consolidation/groups/${gid}`);
      setDetails((d) => ({ ...d, [gid]: r.data }));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const addMember = async (gid) => {
    const tenant_id = newMember[gid]?.tenant_id;
    const ownership_pct = Number(newMember[gid]?.ownership_pct || 100);
    if (!tenant_id) return;
    try {
      await api.post(`/consolidation/groups/${gid}/members`, { tenant_id, ownership_pct });
      toast.success("Member added");
      const r = await api.get(`/consolidation/groups/${gid}`);
      setDetails((d) => ({ ...d, [gid]: r.data }));
      setNewMember((n) => ({ ...n, [gid]: {} }));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const removeMember = async (gid, tenant_id) => {
    if (!window.confirm(`Remove ${tenant_id} from this group?`)) return;
    try {
      await api.delete(`/consolidation/groups/${gid}/members/${tenant_id}`);
      toast.success("Member removed");
      const r = await api.get(`/consolidation/groups/${gid}`);
      setDetails((d) => ({ ...d, [gid]: r.data }));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const deleteGroup = async (gid, name) => {
    if (!window.confirm(`Permanently delete group '${name}'?`)) return;
    try {
      await api.delete(`/consolidation/groups/${gid}`);
      toast.success("Group deleted");
      await onChange();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="p-6" data-testid="consolidation-groups-panel">
      {isSuperuser && (
        <form onSubmit={create} className="grid grid-cols-1 md:grid-cols-5 gap-px bg-zinc-200 border border-zinc-950 mb-6" data-testid="consolidation-create-form">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder="Group name (e.g. Nizwa Holdings)" className="bg-white px-3 py-2.5 text-sm outline-none md:col-span-2" data-testid="consolidation-name" />
          <select value={form.parent_tenant_id} onChange={(e) => setForm({ ...form, parent_tenant_id: e.target.value })} required className="bg-white px-3 py-2.5 text-sm outline-none" data-testid="consolidation-parent">
            <option value="">— Parent tenant —</option>
            {tenants.map((t) => <option key={t.tenant_id} value={t.tenant_id}>{t.name}</option>)}
          </select>
          <select value={form.reporting_currency} onChange={(e) => setForm({ ...form, reporting_currency: e.target.value })} className="bg-white px-3 py-2.5 text-sm outline-none" data-testid="consolidation-currency">
            {["OMR", "USD", "AED", "SAR", "KWD", "BHD", "EUR", "GBP"].map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <button type="submit" disabled={busy} className="bg-zinc-950 text-white text-xs font-semibold px-3 hover:bg-[#FF3E00] disabled:opacity-40 flex items-center justify-center gap-1" data-testid="consolidation-create-btn">
            <Plus size={14} /> {busy ? "CREATING…" : "CREATE GROUP"}
          </button>
        </form>
      )}

      {loading && (
        <div className="text-center py-12 text-zinc-400 text-sm pulse-dot" data-testid="consolidation-loading">Loading groups…</div>
      )}
      {!loading && groups.length === 0 && (
        <div className="text-center py-16 text-zinc-400 text-sm">
          <Buildings size={28} className="mx-auto mb-2 text-zinc-300" />
          {isSuperuser ? "No consolidation groups yet — create one above." : "No consolidation groups accessible to you yet."}
        </div>
      )}

      <div className="space-y-3">
        {groups.map((g) => {
          const d = details[g.id];
          const open = expanded === g.id;
          return (
            <div key={g.id} className="border border-zinc-950" data-testid={`consolidation-group-${g.id}`}>
              <div className="flex items-center justify-between px-4 py-3 bg-white hover:bg-zinc-50 cursor-pointer" onClick={() => expand(g.id)}>
                <div className="flex items-center gap-3">
                  <Stack size={18} weight="fill" className="text-[#FF3E00]" />
                  <div>
                    <div className="font-display font-bold text-base">{g.name}</div>
                    <div className="font-mono text-[10px] text-zinc-500">Parent: {g.parent_tenant_id} · Reporting: {g.reporting_currency} · {g.member_tenant_ids.length} member(s)</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-mono px-2 py-1 border border-zinc-950">{open ? "COLLAPSE" : "EXPAND"}</span>
                  {isSuperuser && (
                    <button onClick={(e) => { e.stopPropagation(); deleteGroup(g.id, g.name); }} className="text-[10px] font-mono border border-[#D0021B] text-[#D0021B] px-2 py-1 hover:bg-[#D0021B] hover:text-white" data-testid={`consolidation-delete-${g.id}`}>DELETE</button>
                  )}
                </div>
              </div>
              {open && d && (
                <div className="border-t border-zinc-200 px-4 py-3 bg-zinc-50">
                  <div className="text-[10px] font-mono text-zinc-500 mb-2">MEMBERS</div>
                  <table className="w-full text-sm mb-3">
                    <thead className="border-b border-zinc-300">
                      <tr className="text-[10px] uppercase tracking-wider text-zinc-500">
                        <th className="text-left py-1.5">Tenant</th>
                        <th className="text-left py-1.5">Currency</th>
                        <th className="text-right py-1.5">Ownership %</th>
                        <th className="text-left py-1.5">Role</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {d.members.map((m) => (
                        <tr key={m.tenant_id} className="border-b border-zinc-200/60">
                          <td className="py-1.5">{m.name}<span className="ml-1 font-mono text-[10px] text-zinc-400">{m.tenant_id}</span></td>
                          <td className="py-1.5 font-mono text-xs">{m.currency}</td>
                          <td className="py-1.5 text-right tnum whitespace-nowrap">{m.ownership_pct.toFixed(1)}%</td>
                          <td className="py-1.5 pl-3">
                            {m.is_parent
                              ? <span className="text-[10px] font-mono px-1.5 py-0.5 bg-zinc-950 text-white">PARENT</span>
                              : <span className="text-[10px] font-mono px-1.5 py-0.5 border border-zinc-300">SUBSIDIARY</span>}
                          </td>
                          <td className="py-1.5 text-right">
                            {isSuperuser && !m.is_parent && (
                              <button onClick={() => removeMember(g.id, m.tenant_id)} className="text-[10px] text-[#D0021B] hover:underline" data-testid={`consolidation-remove-${g.id}-${m.tenant_id}`}>
                                <Trash size={12} className="inline" /> Remove
                              </button>
                            )}
                            {isSuperuser && (
                              <RebaseButton tenantId={m.tenant_id} onChange={() => onChange()} />
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {isSuperuser && (
                    <div className="flex items-center gap-2">
                      <select
                        value={newMember[g.id]?.tenant_id || ""}
                        onChange={(e) => setNewMember((n) => ({ ...n, [g.id]: { ...(n[g.id] || {}), tenant_id: e.target.value } }))}
                        className="border border-zinc-950 bg-white px-2 py-1 text-xs"
                        data-testid={`consolidation-add-tenant-${g.id}`}
                      >
                        <option value="">— Add tenant —</option>
                        {tenants
                          .filter((t) => !g.member_tenant_ids.includes(t.tenant_id))
                          .map((t) => <option key={t.tenant_id} value={t.tenant_id}>{t.name}</option>)}
                      </select>
                      <input
                        type="number"
                        placeholder="Ownership %"
                        min="0" max="100"
                        value={newMember[g.id]?.ownership_pct || ""}
                        onChange={(e) => setNewMember((n) => ({ ...n, [g.id]: { ...(n[g.id] || {}), ownership_pct: e.target.value } }))}
                        className="border border-zinc-950 bg-white px-2 py-1 text-xs w-28 tnum"
                        data-testid={`consolidation-add-pct-${g.id}`}
                      />
                      <button onClick={() => addMember(g.id)} className="text-[10px] font-mono bg-zinc-950 text-white px-3 py-1 hover:bg-[#FF3E00]" data-testid={`consolidation-add-btn-${g.id}`}>
                        <Plus size={11} className="inline" /> ADD MEMBER
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function useConsolidatedReport(groupId, endpoint, params = null) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const qs = params ? new URLSearchParams(Object.entries(params).filter(([, v]) => v)).toString() : "";
  useEffect(() => {
    if (!groupId) { setData(null); return; }
    setLoading(true);
    api.get(`/consolidation/groups/${groupId}/${endpoint}${qs ? "?" + qs : ""}`)
      .then((r) => setData(r.data))
      .catch((err) => toast.error(formatApiError(err.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [groupId, endpoint, qs]);
  return { data, loading };
}

function ConsolidatedPLPanel({ groupId }) {
  const { data, loading } = useConsolidatedReport(groupId, "pl");
  if (!groupId) return <EmptyState />;
  if (loading || !data) return <div className="p-8 text-sm text-zinc-500">Loading…</div>;
  const cur = data.reporting_currency;
  return (
    <div className="p-6" data-testid="consolidated-pl-panel">
      <div className="mb-4 text-xs font-mono text-zinc-400">CONSOLIDATED P&L · {data.group_name} · as of {data.as_of} · {cur}</div>
      <StatementSection title="Revenue" rows={data.revenue} total={data.revenue_total} cur={cur} />
      <StatementSection title="Expenses" rows={data.expenses} total={data.expenses_total} cur={cur} />
      <div className="bg-zinc-950 text-white px-5 py-4 space-y-2">
        <div className="flex justify-between items-center">
          <div className="font-mono text-[10px] tracking-widest">CONSOLIDATED NET PROFIT (100%)</div>
          <div className="font-display font-black text-2xl tnum" data-testid="consolidated-net-profit">{formatCurrency(data.net_profit, cur)}</div>
        </div>
        {(data.net_profit_nci || 0) !== 0 && (
          <>
            <div className="flex justify-between text-xs text-zinc-300">
              <div className="font-mono tracking-wider">− NCI SHARE (IFRS 10)</div>
              <div className="tnum" data-testid="consolidated-nci-profit">{formatCurrency(data.net_profit_nci, cur)}</div>
            </div>
            <div className="border-t border-zinc-700 pt-2 flex justify-between items-center">
              <div className="font-mono text-[10px] tracking-widest text-[#FF3E00]">= NET PROFIT · PARENT SHARE</div>
              <div className="font-display font-black text-xl tnum text-[#FF3E00]" data-testid="consolidated-parent-profit">{formatCurrency(data.net_profit_parent, cur)}</div>
            </div>
          </>
        )}
      </div>
      {data.nci?.per_entity?.length > 0 && (
        <div className="mt-4 border border-amber-200 bg-amber-50 p-3 text-xs" data-testid="consolidated-nci-detail">
          <div className="font-mono text-[10px] tracking-widest text-amber-800 mb-1">NCI PROFIT BREAKDOWN · IFRS 10</div>
          {data.nci.per_entity.map((n) => (
            <div key={n.tenant_id} className="flex justify-between py-0.5">
              <span>{n.name} <span className="text-zinc-500">({n.minority_pct}% minority)</span></span>
              <span className="tnum font-semibold">{formatCurrency(n.amount, cur)}</span>
            </div>
          ))}
        </div>
      )}
      <PerEntityTable rows={data.per_entity} cur={cur} fxSources={data.fx_sources} />
    </div>
  );
}

function ConsolidatedBSPanel({ groupId }) {
  const { data, loading } = useConsolidatedReport(groupId, "balance-sheet");
  if (!groupId) return <EmptyState />;
  if (loading || !data) return <div className="p-8 text-sm text-zinc-500">Loading…</div>;
  const cur = data.reporting_currency;
  return (
    <div className="p-6" data-testid="consolidated-bs-panel">
      <div className="mb-4 flex items-center justify-between">
        <div className="text-xs font-mono text-zinc-400">CONSOLIDATED BALANCE SHEET · {data.group_name} · as of {data.as_of} · {cur}</div>
        <span className={`text-[10px] font-mono px-2 py-0.5 ${data.balanced ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-[#D0021B]"}`} data-testid="consolidated-bs-status">
          {data.balanced ? "BALANCED ✓" : "UNBALANCED"}
        </span>
      </div>
      <StatementSection title="Assets" rows={data.assets} total={data.assets_total} cur={cur} />
      <StatementSection title="Liabilities" rows={data.liabilities} total={data.liabilities_total} cur={cur} />
      <StatementSection title="Equity" rows={data.equity} total={data.equity_total} cur={cur} />
      <PerEntityTable rows={data.per_entity} cur={cur} fxSources={data.fx_sources} showBalancedBadge={true} />
    </div>
  );
}

function ConsolidatedCashPanel({ groupId }) {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const { data, loading } = useConsolidatedReport(groupId, "cash-position", { from, to });
  const applyPreset = (f, t) => { setFrom(f); setTo(t); };
  const clearFilter = () => { setFrom(""); setTo(""); };
  if (!groupId) return <EmptyState />;
  if (loading || !data) return <div className="p-8 text-sm text-zinc-500">Loading…</div>;
  const cur = data.reporting_currency;
  const hasFilter = Boolean(from || to);
  return (
    <div className="p-6" data-testid="consolidated-cash-panel">
      <div className="mb-4 text-xs font-mono text-zinc-400">CONSOLIDATED CASH POSITION · {data.group_name} · {cur}</div>

      {/* AR/AP window filter — cash balance is always as-of-now */}
      <div className="flex items-end gap-3 mb-4 flex-wrap" data-testid="cash-position-filter">
        <DatePicker label="AR/AP From" value={from} onChange={setFrom} placeholder="Any date" testId="cash-position-from" />
        <DatePicker label="AR/AP To" value={to} onChange={setTo} placeholder="Any date" testId="cash-position-to" />
        {hasFilter && (
          <button onClick={clearFilter} data-testid="cash-position-clear-filter" className="text-[10px] font-mono text-zinc-500 hover:text-[#FF3E00] flex items-center gap-1 pb-1">
            <X size={12} /> CLEAR
          </button>
        )}
        <div className="ml-auto pb-0.5">
          <FiscalPresetChips from={from} to={to} onApply={applyPreset} testIdPrefix="cash-position-preset" />
        </div>
      </div>
      {hasFilter && (
        <div className="mb-4 bg-amber-50 border border-amber-200 px-3 py-1.5 text-[11px] font-mono text-amber-800" data-testid="cash-position-filter-hint">
          AR / AP figures scoped to invoices with invoice_date {from ? `≥ ${from}` : "*"} {to ? `and ≤ ${to}` : ""}. Cash balances stay as-of-now.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-zinc-200 border border-zinc-950 mb-6">
        <StatCell label="Cash Total (100%)" value={data.cash_total} cur={cur} />
        <StatCell label="AR (Open)" value={data.ar_total} cur={cur} />
        <StatCell label="AP (Open)" value={data.ap_total} cur={cur} />
        <StatCell label="Net Cash Position" value={data.net_cash_position} cur={cur} accent testid="consolidated-net-cash" />
      </div>
      {(data.nci_cash_total || 0) !== 0 && (
        <div className="mb-4 bg-amber-50 border border-amber-200 px-4 py-2 text-xs flex items-center justify-between" data-testid="consolidated-nci-cash-disclosure">
          <span className="font-mono tracking-widest text-amber-800">NCI CASH DISCLOSURE (IFRS 10)</span>
          <span className="tnum font-semibold" data-testid="consolidated-nci-cash-total">{formatCurrency(data.nci_cash_total, cur)}</span>
        </div>
      )}
      <div className="text-[10px] font-mono text-zinc-500 mb-2">PER-ENTITY</div>
      <table className="w-full text-sm">
        <thead className="bg-zinc-50 border-b border-zinc-950">
          <tr className="text-[10px] uppercase tracking-wider">
            <th className="text-left px-3 py-2">Entity</th>
            <th className="text-left px-3 py-2">Local Ccy</th>
            <th className="text-right px-3 py-2">Local Cash</th>
            <th className="text-right px-3 py-2">Local AR</th>
            <th className="text-right px-3 py-2">Reporting Cash</th>
            <th className="text-right px-3 py-2">Reporting AR</th>
            <th className="text-right px-3 py-2">NCI Cash</th>
            <th className="text-right px-3 py-2">Own %</th>
          </tr>
        </thead>
        <tbody>
          {data.per_entity.map((e) => (
            <tr key={e.tenant_id} className="border-b border-zinc-100">
              <td className="px-3 py-2">{e.name}</td>
              <td className="px-3 py-2 font-mono text-xs">{e.local_currency}</td>
              <td className="px-3 py-2 text-right tnum">{formatCurrency(e.local_cash, e.local_currency)}</td>
              <td className="px-3 py-2 text-right tnum">{formatCurrency(e.local_ar, e.local_currency)}</td>
              <td className="px-3 py-2 text-right tnum font-semibold">{formatCurrency(e.reporting_cash, cur)}</td>
              <td className="px-3 py-2 text-right tnum">{formatCurrency(e.reporting_ar, cur)}</td>
              <td className="px-3 py-2 text-right tnum text-amber-700">{e.nci_cash ? formatCurrency(e.nci_cash, cur) : "—"}</td>
              <td className="px-3 py-2 text-right tnum">{e.ownership_pct.toFixed(0)}%</td>
            </tr>
          ))}
        </tbody>
      </table>
      <CashRunwayChart groupId={groupId} cur={cur} />
    </div>
  );
}

function StatCell({ label, value, cur, accent, testid }) {
  return (
    <div className={`bg-white px-4 py-3 ${accent ? "bg-zinc-950 text-white" : ""}`} data-testid={testid}>
      <div className={`label-sm ${accent ? "text-zinc-400" : "text-zinc-500"}`}>{label}</div>
      <div className="font-display font-black text-xl tnum mt-1">{formatCurrency(value, cur)}</div>
    </div>
  );
}

function StatementSection({ title, rows, total, cur }) {
  return (
    <div className="mb-5">
      <div className="flex justify-between border-b-2 border-zinc-950 pb-1 mb-2">
        <div className="font-mono text-[10px] tracking-widest uppercase">{title}</div>
        <div className="font-semibold tnum">{formatCurrency(total, cur)}</div>
      </div>
      {rows.length === 0 ? (
        <div className="text-xs text-zinc-400 py-2">No entries.</div>
      ) : (
        <table className="w-full text-sm">
          <tbody>
            {rows.map((r, i) => (
              <tr key={`${r.code}-${i}`} className="border-b border-zinc-100">
                <td className="py-1.5 text-xs font-mono text-zinc-500 w-16">{r.code}</td>
                <td className="py-1.5">{r.name}</td>
                <td className="py-1.5 text-right tnum">{formatCurrency(r.amount, cur)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function PerEntityTable({ rows, cur, fxSources, showBalancedBadge }) {
  return (
    <div className="mt-6">
      <div className="text-[10px] font-mono text-zinc-500 mb-2">PER-ENTITY CONTRIBUTION · reporting {cur} (100% consolidated)</div>
      <table className="w-full text-sm">
        <thead className="bg-zinc-50 border-b border-zinc-950">
          <tr className="text-[10px] uppercase tracking-wider">
            <th className="text-left px-3 py-2">Entity</th>
            <th className="text-left px-3 py-2">Local</th>
            <th className="text-right px-3 py-2">FX Rate</th>
            <th className="text-right px-3 py-2">Own %</th>
            <th className="text-right px-3 py-2">Contribution ({cur})</th>
            {showBalancedBadge && <th className="text-left px-3 py-2">BS Check</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((e) => (
            <tr key={e.tenant_id} className="border-b border-zinc-100" data-testid={`consolidated-entity-row-${e.tenant_id}`}>
              <td className="px-3 py-2">{e.name}</td>
              <td className="px-3 py-2 font-mono text-xs">{e.local_currency}</td>
              <td className="px-3 py-2 text-right tnum text-xs">{e.fx_rate_to_reporting ? e.fx_rate_to_reporting.toFixed(6) : "—"}</td>
              <td className="px-3 py-2 text-right tnum">{e.ownership_pct.toFixed(0)}%</td>
              <td className="px-3 py-2 text-right tnum font-semibold">{formatCurrency(e.grand_total, cur)}</td>
              {showBalancedBadge && (
                <td className="px-3 py-2">
                  {e.bs_balanced === true ? (
                    <span className="text-[10px] font-mono px-1.5 py-0.5 bg-emerald-50 text-emerald-700" data-testid={`entity-balanced-${e.tenant_id}`}>BALANCED ✓</span>
                  ) : e.bs_balanced === false ? (
                    <span className="text-[10px] font-mono px-1.5 py-0.5 bg-red-50 text-[#D0021B]" data-testid={`entity-unbalanced-${e.tenant_id}`} title={`Δ ${e.bs_delta}`}>UNBALANCED · Δ {formatCurrency(e.bs_delta, cur)}</span>
                  ) : (
                    <span className="text-[10px] font-mono text-zinc-400">—</span>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {fxSources?.length > 0 && (
        <div className="mt-3 text-[10px] font-mono text-zinc-500">
          FX APPLIED: {fxSources.map((s) => `${s.tenant} ${s.from}→${s.to}@${s.rate?.toFixed(6) ?? "n/a"}`).join(" · ")}
        </div>
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="p-12 text-center text-zinc-400">
      <Buildings size={32} className="mx-auto mb-2 text-zinc-300" />
      <div className="text-sm">Select a group above to view its consolidated report.</div>
    </div>
  );
}


function BoardSnapshotActions({ groupId }) {
  const [busyEmail, setBusyEmail] = useState(false);
  const [busyPdf, setBusyPdf] = useState(false);
  const [lastRun, setLastRun] = useState(null);
  const qs = groupId ? `?group_id=${groupId}` : "";

  const preview = async () => {
    setBusyPdf(true);
    try {
      const r = await api.post(`/board-snapshot/preview${qs}`, null, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      if (err.response?.data instanceof Blob) {
        const text = await err.response.data.text();
        try { const j = JSON.parse(text); toast.error(j.detail || "Preview failed"); }
        catch { toast.error("Preview failed"); }
      } else {
        toast.error(formatApiError(err.response?.data?.detail));
      }
    } finally { setBusyPdf(false); }
  };

  const sendNow = async () => {
    if (!window.confirm("Send the Board Snapshot email now to all admins/owners of this group?")) return;
    setBusyEmail(true);
    try {
      const r = await api.post(`/board-snapshot/send-now${qs}`);
      setLastRun(r.data);
      if (r.data.ok) {
        toast.success(`Snapshot sent to ${r.data.recipients?.length || 0} recipient(s)`);
      } else if (r.data.mocked) {
        toast.info("Email dispatch is mocked (no RESEND_API_KEY) — audit log created");
      } else {
        toast.error(r.data.reason || r.data.error || "Send failed");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusyEmail(false); }
  };

  return (
    <div className="ml-auto flex items-center gap-2" data-testid="board-snapshot-actions">
      <button
        onClick={preview}
        disabled={busyPdf}
        className="text-[10px] font-mono border border-zinc-950 px-3 py-1 hover:bg-zinc-950 hover:text-white flex items-center gap-1 disabled:opacity-40"
        data-testid="board-snapshot-preview-btn"
      >
        <FilePdf size={12} /> {busyPdf ? "BUILDING…" : "PREVIEW PDF"}
      </button>
      <button
        onClick={sendNow}
        disabled={busyEmail}
        className="text-[10px] font-mono bg-zinc-950 text-white px-3 py-1 hover:bg-[#FF3E00] flex items-center gap-1 disabled:opacity-40"
        data-testid="board-snapshot-send-btn"
      >
        <EnvelopeSimple size={12} weight="fill" /> {busyEmail ? "SENDING…" : "EMAIL SNAPSHOT NOW"}
      </button>
      {lastRun && (
        <span className="text-[10px] font-mono text-zinc-500" data-testid="board-snapshot-last-run">
          Last: {lastRun.ok ? "delivered" : lastRun.mocked ? "mocked" : "failed"} · {new Date(lastRun.at).toLocaleTimeString()}
        </span>
      )}
    </div>
  );
}

function RebaseButton({ tenantId, onChange }) {
  const [busy, setBusy] = useState(false);

  const rebase = async () => {
    if (!window.confirm(`Rebase tenant '${tenantId}'?\n\nThis patches the Cash account balance to reconcile a broken opening seed (Cash=0 vs Equity=100k). It is a data-migration operation — recorded in rebase_events for audit.`)) return;
    setBusy(true);
    try {
      // Diagnose first
      const d = await api.get(`/consolidation/rebase/${tenantId}/diagnose`);
      if (d.data.balanced) {
        toast.info(`${tenantId} is already balanced (Δ ${d.data.delta}) — no action taken`);
        setBusy(false);
        return;
      }
      const r = await api.post(`/consolidation/rebase/${tenantId}`);
      if (r.data.rebased) {
        toast.success(`Rebased. Cash ${r.data.cash_before} → ${r.data.cash_after}. Δ ${r.data.delta_before}`);
      } else {
        toast.info(r.data.reason || "No changes");
      }
      onChange?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <button
      onClick={rebase}
      disabled={busy}
      className="ml-2 text-[10px] font-mono border border-amber-600 text-amber-700 px-2 py-0.5 hover:bg-amber-600 hover:text-white disabled:opacity-40"
      data-testid={`rebase-btn-${tenantId}`}
      title="Balanced Rebase — fix historic Cash=0/Equity=100k opening seed"
    >
      {busy ? "REBASING…" : "REBASE"}
    </button>
  );
}


function CashAlertConfigPanel({ groupId, isSuperuser, parentTenantId, currentUserTenantId }) {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ threshold: "", enabled: true, channel: "email", slack_webhook_url: "" });
  const [busy, setBusy] = useState(false);
  const canEdit = isSuperuser || currentUserTenantId === parentTenantId;

  const load = async () => {
    if (!groupId) { setCfg(null); setLoading(false); return; }
    setLoading(true);
    try {
      const r = await api.get(`/consolidation/groups/${groupId}/cash-alert`);
      if (r.data.configured) {
        setCfg(r.data);
        setForm({
          threshold: r.data.threshold || "",
          enabled: r.data.enabled !== false,
          channel: r.data.channel || "email",
          slack_webhook_url: r.data.slack_webhook_url || "",
        });
      } else {
        setCfg(null);
        setForm({ threshold: "", enabled: true, channel: "email", slack_webhook_url: "" });
      }
    } catch (err) {
      // Non-member — hide the panel silently.
      setCfg(null);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [groupId]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (e) => {
    e.preventDefault();
    if (!canEdit) return;
    setBusy(true);
    try {
      await api.put(`/consolidation/groups/${groupId}/cash-alert`, {
        threshold: Number(form.threshold),
        enabled: form.enabled,
        channel: form.channel,
        slack_webhook_url: form.channel === "email" ? null : form.slack_webhook_url,
      });
      toast.success("Cash alert configured");
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const evaluateNow = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/consolidation/groups/${groupId}/cash-alert/evaluate`);
      if (r.data.triggered) {
        toast.success(`Alert TRIGGERED · cash ${r.data.cash} < threshold ${r.data.threshold}`);
      } else {
        toast.info(`Cash ${r.data.cash} is above threshold ${r.data.threshold} — no alert sent`);
      }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("Remove the cash alert configuration?")) return;
    try {
      await api.delete(`/consolidation/groups/${groupId}/cash-alert`);
      toast.success("Cash alert removed");
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (!groupId || loading) return null;

  return (
    <div className="p-6 border-t border-zinc-200 bg-amber-50/40" data-testid="cash-alert-config">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="font-mono text-[10px] tracking-widest text-amber-800">GROUP CASH ALERT · Daily 07:00 UTC cron</div>
          <div className="text-xs text-zinc-600 mt-0.5">Ping the CFO on email or Slack when consolidated cash drops below a threshold.</div>
        </div>
        {cfg && (
          <span className="text-[10px] font-mono px-2 py-0.5 bg-emerald-100 text-emerald-800" data-testid="cash-alert-status">
            CONFIGURED · threshold {cfg.threshold} · {cfg.channel}
          </span>
        )}
      </div>
      {!canEdit ? (
        <div className="text-xs text-zinc-500 italic">Only the parent tenant admin or superuser can configure this alert.</div>
      ) : (
        <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-[140px_120px_1fr_auto] gap-2 items-center">
          <input
            type="number"
            step="0.001"
            required
            value={form.threshold}
            onChange={(e) => setForm({ ...form, threshold: e.target.value })}
            placeholder="Threshold"
            className="border border-zinc-950 bg-white px-2 py-1.5 text-sm tnum"
            data-testid="cash-alert-threshold"
          />
          <select
            value={form.channel}
            onChange={(e) => setForm({ ...form, channel: e.target.value })}
            className="border border-zinc-950 bg-white px-2 py-1.5 text-sm"
            data-testid="cash-alert-channel"
          >
            <option value="email">Email</option>
            <option value="slack">Slack</option>
            <option value="both">Email + Slack</option>
          </select>
          {(form.channel === "slack" || form.channel === "both") ? (
            <input
              type="url"
              required
              value={form.slack_webhook_url}
              onChange={(e) => setForm({ ...form, slack_webhook_url: e.target.value })}
              placeholder="https://hooks.slack.com/services/…"
              className="border border-zinc-950 bg-white px-2 py-1.5 text-sm font-mono text-xs"
              data-testid="cash-alert-slack-url"
            />
          ) : (
            <label className="flex items-center gap-2 text-xs text-zinc-600">
              <input
                type="checkbox"
                checked={form.enabled}
                onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                className="accent-[#FF3E00]"
                data-testid="cash-alert-enabled"
              />
              Enabled (cron will run daily)
            </label>
          )}
          <div className="flex items-center gap-2">
            <button
              type="submit"
              disabled={busy}
              className="bg-zinc-950 text-white text-xs font-semibold px-3 py-1.5 hover:bg-[#FF3E00] disabled:opacity-40"
              data-testid="cash-alert-save"
            >SAVE</button>
            {cfg && (
              <>
                <button type="button" onClick={evaluateNow} disabled={busy} className="text-[10px] font-mono border border-zinc-950 px-2 py-1 hover:bg-zinc-950 hover:text-white" data-testid="cash-alert-evaluate">
                  EVALUATE NOW
                </button>
                <button type="button" onClick={remove} className="text-[10px] font-mono text-[#D0021B] hover:underline" data-testid="cash-alert-remove">
                  Remove
                </button>
              </>
            )}
          </div>
        </form>
      )}
    </div>
  );
}


function CashRunwayChart({ groupId, cur }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!groupId) return;
    setLoading(true);
    api.get(`/consolidation/groups/${groupId}/cash-runway?weeks=12`)
      .then((r) => setData(r.data))
      .catch((err) => toast.error(formatApiError(err.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [groupId]);

  if (loading) return <div className="mt-6 text-sm text-zinc-500 pulse-dot" data-testid="cash-runway-loading">Loading cash runway…</div>;
  if (!data) return null;

  return (
    <div className="mt-8" data-testid="cash-runway-chart">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="font-mono text-[10px] tracking-widest text-zinc-500">12-WEEK CASH RUNWAY FORECAST</div>
          <div className="text-xs text-zinc-500">Projected cash trajectory based on scheduled AR/AP due dates.</div>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <div><span className="font-mono text-zinc-500">MIN:</span> <span className="tnum font-semibold">{formatCurrency(data.min_cash, cur)}</span></div>
          <div><span className="font-mono text-zinc-500">FINAL:</span> <span className="tnum font-semibold">{formatCurrency(data.final_cash, cur)}</span></div>
          <div className={data.runway_week ? "text-[#D0021B]" : "text-emerald-700"} data-testid="cash-runway-status">
            {data.runway_week
              ? <><span className="font-mono">RUNWAY ENDS:</span> <b>Week {data.runway_week}</b></>
              : <span className="font-mono">RUNWAY: SUFFICIENT ✓</span>}
          </div>
        </div>
      </div>
      <div className="h-64 border border-zinc-950 bg-zinc-50">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data.series} margin={{ top: 12, right: 24, left: 12, bottom: 12 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
            <XAxis dataKey="week" tick={{ fontSize: 10, fontFamily: "monospace" }} label={{ value: "Week", position: "bottom", offset: -6, fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10, fontFamily: "monospace" }} tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v.toString())} />
            <Tooltip
              contentStyle={{ background: "#0a0a0a", color: "white", border: "none", fontSize: 12 }}
              formatter={(v, n) => [formatCurrency(v, cur), n === "cash" ? "Cash" : n]}
              labelFormatter={(l, payload) => `Week ${l}${payload?.[0]?.payload?.week_start ? ` · ${payload[0].payload.week_start}` : ""}`}
            />
            <ReferenceLine y={0} stroke="#D0021B" strokeDasharray="4 4" label={{ value: "Zero", fontSize: 10, fill: "#D0021B" }} />
            <Line type="monotone" dataKey="cash" stroke="#FF3E00" strokeWidth={2.5} dot={{ r: 3, fill: "#FF3E00" }} activeDot={{ r: 5 }} isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <details className="mt-3 text-xs" data-testid="cash-runway-buckets">
        <summary className="cursor-pointer font-mono text-[10px] tracking-widest text-zinc-500 hover:text-zinc-950">WEEKLY DETAILS ▾</summary>
        <table className="w-full mt-2 text-xs">
          <thead className="bg-zinc-50 border-b border-zinc-300">
            <tr className="text-[9px] uppercase tracking-wider">
              <th className="text-left px-2 py-1">Week</th>
              <th className="text-right px-2 py-1">AR In</th>
              <th className="text-right px-2 py-1">AP Out</th>
              <th className="text-right px-2 py-1">Net</th>
              <th className="text-right px-2 py-1">Cash After</th>
            </tr>
          </thead>
          <tbody>
            {data.buckets.map((b, i) => (
              <tr key={b.week_start} className="border-b border-zinc-100">
                <td className="px-2 py-1 font-mono">{i + 1} · {b.week_start}</td>
                <td className="px-2 py-1 text-right tnum text-emerald-700">{b.ar_in > 0 ? formatCurrency(b.ar_in, cur) : "—"}</td>
                <td className="px-2 py-1 text-right tnum text-[#D0021B]">{b.ap_out > 0 ? formatCurrency(b.ap_out, cur) : "—"}</td>
                <td className="px-2 py-1 text-right tnum">{formatCurrency(b.net, cur)}</td>
                <td className="px-2 py-1 text-right tnum font-semibold">{formatCurrency(data.series[i + 1]?.cash || 0, cur)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </div>
  );
}

function HistoryChart({ groupId, cur }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    if (!groupId) return;
    api.get(`/consolidation/groups/${groupId}/history`)
      .then((r) => setData(r.data))
      .catch(() => setData({ count: 0, history: [] }));
  };
  useEffect(() => { load(); }, [groupId]); // eslint-disable-line react-hooks/exhaustive-deps

  const capture = async () => {
    setBusy(true);
    try {
      await api.post(`/consolidation/groups/${groupId}/history/snapshot`);
      toast.success("Snapshot captured");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!data) return null;
  const chartData = (data.history || []).map((s) => ({
    label: s.label,
    net_profit: s.pl?.net_profit || 0,
    assets: s.bs?.assets_total || 0,
    cash: s.cash?.cash_total || 0,
  }));

  return (
    <div className="mt-8 border border-zinc-950" data-testid="history-chart">
      <div className="bg-zinc-950 text-white px-4 py-2 flex items-center justify-between">
        <div className="font-mono text-[10px] tracking-widest">CONSOLIDATION HISTORY · {data.count} snapshot(s)</div>
        <button onClick={capture} disabled={busy} className="text-[10px] font-mono border border-white text-white px-3 py-0.5 hover:bg-white hover:text-zinc-950 disabled:opacity-40" data-testid="history-capture-btn">
          {busy ? "CAPTURING…" : "CAPTURE NOW"}
        </button>
      </div>
      <div className="p-4">
        {chartData.length === 0 ? (
          <div className="text-xs text-zinc-500 italic">No snapshots yet. Click CAPTURE NOW to save the current numbers, or wait for the 1st-of-month cron.</div>
        ) : (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 12, right: 24, left: 12, bottom: 12 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                <XAxis dataKey="label" tick={{ fontSize: 10, fontFamily: "monospace" }} />
                <YAxis tick={{ fontSize: 10, fontFamily: "monospace" }} tickFormatter={(v) => (Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(1)}k` : v.toString())} />
                <Tooltip
                  contentStyle={{ background: "#0a0a0a", color: "white", border: "none", fontSize: 11 }}
                  formatter={(v, n) => [formatCurrency(v, cur), n]}
                />
                <Legend wrapperStyle={{ fontSize: 11, fontFamily: "monospace" }} />
                <Bar dataKey="cash" fill="#FF3E00" name="Cash" />
                <Bar dataKey="net_profit" fill="#111111" name="Net Profit" />
                <Bar dataKey="assets" fill="#0F766E" name="Assets" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}

